'use client';
import { useCallback, useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Check, ChevronDown, type LucideIcon } from 'lucide-react';
import { Button } from './Button';
import { ItemBadges, ManageSelectionModal, type ManageSelectionItem } from './ManageSelectionModal';
import { SelectionSummary } from './SelectionSummary';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './shadcn/command';
import { choiceCheckClass, choiceItemClass, selectTriggerVariants } from './shadcn/select';
import { Tooltip, TooltipAnchor, TooltipContent } from './shadcn/tooltip';
import { useTooltipAnchor } from './shadcn/useTooltipAnchor';
import { LabelMark, StatusGlyph } from './StatusMark';
import { cn } from '../../lib/utils';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useMobile } from '../../lib/useMobile';
import { normalizeText } from '../../lib/normalizeText';

/** A dropdown shows a search box only when the list is long enough to need one. */
const SEARCH_THRESHOLD = 8;

/** cmdk derives an item's value from its text when the value is empty, and a pinned row's id is `''`; the
 *  fixed prefix keeps every row's value non-empty and unique. */
const ROW_VALUE_PREFIX = 'row:';

/** One trigger for a settings record. `popup` says what it opens: a `dialog` (the default, for document
 *  editors and the custom-skin manager, where the caller owns opening and persistence) or an anchored
 *  `listbox` ({@link RowPicker}, which makes it a combobox). It IS the Select's trigger surface
 *  (`selectTriggerVariants`), so a record's row picker and its Select are one height, one width, one touch
 *  floor and one open state: the primary frame, wash and ink a Popover trigger gets from `data-state="open"`.
 *  The trigger uses the Select default and owns the field name, value and decorative icons.
 *
 *  `iconOnly` is that same trigger shaped as the shared square ghost icon button instead: the control
 *  then does not HOLD a value but starts an action, so there is no value to draw — it takes the step of
 *  the icon buttons beside it in a toolbar or a bulk bar, which a field-width trigger cannot (a labelled
 *  trigger wrapped a phone's selection bar onto two lines). `label` is its accessible name and its hover
 *  tooltip, `icon` its glyph, `summary` is not drawn, and the open state is the shared wash. */
export function RowPickerTrigger({ label, summary, icon, trailingIcon: TrailingIcon = ChevronDown, popup = 'dialog', iconOnly = false, className = '', ...props }: {
  label: string;
  summary: string;
  icon?: ReactNode;
  trailingIcon?: LucideIcon;
  popup?: 'dialog' | 'listbox';
  iconOnly?: boolean;
} & Omit<ComponentProps<'button'>, 'children' | 'aria-label' | 'aria-haspopup' | 'role' | 'type'>) {
  // An anchored listbox makes the trigger a combobox; a dialog trigger stays a plain button.
  const combobox = popup === 'listbox';
  // An action trigger without a glyph would be a nameless square: refuse it here rather than draw it.
  if (iconOnly && !icon) throw new Error(`An icon-only row picker needs an icon ("${label}")`);
  if (iconOnly) {
    return (
      <Button
        {...props}
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        title={label}
        role={combobox ? 'combobox' : undefined}
        aria-haspopup={popup}
        data-row-picker
        // The open state is the ghost's own hover wash: an icon trigger has no frame to switch to primary,
        // so the shared pressed look is what tells the reader the dropdown is up.
        className={cn('data-[state=open]:bg-accent data-[state=open]:text-accent-foreground', className)}
      >
        <span aria-hidden className="flex shrink-0">{icon}</span>
      </Button>
    );
  }
  return (
    <button
      {...props}
      type="button"
      aria-label={label}
      role={combobox ? 'combobox' : undefined}
      aria-haspopup={popup}
      data-row-picker
      className={cn(selectTriggerVariants({}), 'justify-between', className)}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {icon ? <span aria-hidden className="flex shrink-0">{icon}</span> : null}
        <span className="min-w-0 truncate text-left">{summary}</span>
      </span>
      <TrailingIcon size={14} aria-hidden className="shrink-0 opacity-60 transition-transform group-data-[state=open]:rotate-180" />
    </button>
  );
}

/** What a query is matched against: the label, the group's display name and the badge text, so typing
 *  "anthropic" finds a provider's models and "oauth" finds the accounts that carry the badge. */
const keywordsOf = (item: RowPickerItem): string[] => [
  item.label,
  ...(item.description ? [item.description] : []),
  ...(item.group === '' ? [] : [item.groupLabel ?? item.group]),
  ...(item.badges?.map((badge) => badge.text) ?? []),
  ...(item.labels?.map((label) => label.text) ?? []),
  ...(item.status ? [item.status.label] : []),
];

/** One dropdown row. Beyond the multi picker's row it may carry a muted second line and one trailing
 *  pointer control (the voice sample player is the consumer). Host-only: the plugin kit's `RowPickerItem`
 *  does not publish either. */
export interface RowPickerItem extends ManageSelectionItem {
  /** A muted line under the label; also matched by the search. */
  description?: string;
  /** A small control on the row's right edge, after the check. Its press never picks the row and does not
   *  take focus from the list, so arrows and Enter keep driving the dropdown. */
  action?: ReactNode;
}

/** cmdk's own filter is not diacritics-aware; this one folds case and accents over every keyword. */
const rowFilter = (_value: string, search: string, keywords: string[] = []): number =>
  normalizeText(keywords.join(' ')).includes(normalizeText(search.trim())) ? 1 : 0;

function RowOption({ item, current, highlighted, onPick, onDismiss }: { item: RowPickerItem; current: boolean; highlighted: boolean; onPick: (id: string) => void; onDismiss: () => void }) {
  const anchor = useTooltipAnchor();
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { setDismissed(false); }, [highlighted]);
  const option = (
    <CommandItem
      value={ROW_VALUE_PREFIX + item.id}
      keywords={keywordsOf(item)}
      disabled={item.disabled}
      data-status-inspectable={item.disabled && item.status ? '' : undefined}
      title={item.disabled ? item.disabledHint : undefined}
      onSelect={() => onPick(item.id)}
      // The Select's row and its current-pick wash; cmdk keeps its own `data-selected` highlight.
      data-state={current ? 'checked' : 'unchecked'}
      className={choiceItemClass}
    >
      {item.icon ? <span aria-hidden className="flex shrink-0">{item.icon}</span> : null}
      {item.description ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{item.label}</span>
          <span className="truncate text-meta text-muted-foreground">{item.description}</span>
        </span>
      ) : <span className="min-w-0 flex-1 truncate">{item.label}</span>}
      <ItemBadges badges={item.badges} />
      {item.labels?.map((label) => <LabelMark key={label.text} tone={label.tone}>{label.text}</LabelMark>)}
      {item.status ? <><StatusGlyph {...item.status} /><span className="sr-only">{item.status.label}</span></> : null}
      {current ? <Check size={15} aria-hidden className={choiceCheckClass} /> : null}
      {item.action ? (
        // cmdk picks a row on click, and a pressed button would take focus from the listbox.
        <span className="flex shrink-0" onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.preventDefault()}>
          {item.action}
        </span>
      ) : null}
    </CommandItem>
  );
  if (!item.status) return option;
  return <Tooltip open={anchor.open || (highlighted && !dismissed)} onOpenChange={(open) => { anchor.onOpenChange(open); if (!open) setDismissed(true); }}>
    <TooltipAnchor asChild>
      <div {...anchor.hover} onClick={item.disabled ? (event) => { event.preventDefault(); event.stopPropagation(); anchor.show(); } : undefined}>{option}</div>
    </TooltipAnchor>
    <TooltipContent className="w-auto max-w-[min(--spacing(80),calc(100vw-var(--spacing)*6))] break-all px-2 py-1" side="top" onEscapeKeyDown={(event) => { event.preventDefault(); onDismiss(); }}>{item.status.label}</TooltipContent>
  </Tooltip>;
}

/** The open dropdown's body. It mounts with the popover, so the highlight starts on the current pick and
 *  the search starts empty on every opening. */
function RowPickerList({ label, items, value, groupIcons, notice, searchable, listRef, inputRef, onPick, onDismiss }: {
  label: string;
  items: RowPickerItem[];
  value: string;
  groupIcons?: Record<string, ReactNode>;
  notice?: string;
  searchable: boolean;
  listRef: React.Ref<HTMLDivElement>;
  inputRef: React.Ref<HTMLInputElement>;
  onPick: (id: string) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [highlight, setHighlight] = useState(ROW_VALUE_PREFIX + value);
  const pinned = items.filter((item) => item.group === '');
  // Unique groups in first-appearance order, like the multi picker's sections.
  const groups = new Map<string, string>();
  for (const item of items) if (item.group !== '' && !groups.has(item.group)) groups.set(item.group, item.groupLabel ?? item.group);
  return (
    <Command
      label={t.managePicker.searchPlaceholder}
      filter={rowFilter}
      value={highlight}
      onValueChange={setHighlight}
      onKeyDown={(event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter')) return;
        const options = [...event.currentTarget.querySelectorAll<HTMLElement>('[cmdk-item]')]
          .filter((node) => node.getAttribute('aria-disabled') !== 'true' || node.hasAttribute('data-status-inspectable'));
        // cmdk normalizes values for its DOM; read its selected row rather than comparing a caller's id.
        const active = options.findIndex((node) => node.getAttribute('aria-selected') === 'true');
        if (event.key === 'Enter') {
          if (options[active]?.hasAttribute('data-status-inspectable')) event.preventDefault();
          return;
        }
        const next = options[active + (event.key === 'ArrowDown' ? 1 : -1)];
        if (!next) {
          if (options[active]?.hasAttribute('data-status-inspectable')) event.preventDefault();
          return;
        }
        if (!(next.hasAttribute('data-status-inspectable') || options[active]?.hasAttribute('data-status-inspectable'))) return;
        event.preventDefault();
        const nextValue = next.getAttribute('data-value');
        if (nextValue === null) throw new Error('RowPicker option has no value');
        setHighlight(nextValue);
        const focusTarget = event.target;
        if (focusTarget instanceof HTMLElement) focusTarget.setAttribute('aria-activedescendant', next.id);
        next.scrollIntoView?.({ block: 'nearest' });
      }}
      className="h-auto max-h-[min(--spacing(96),var(--radix-popover-content-available-height))] outline-none"
    >
      {searchable ? <CommandInput ref={inputRef} placeholder={t.managePicker.searchPlaceholder} /> : null}
      {notice ? <p className="px-3 py-2 text-meta italic text-muted-foreground">{notice}</p> : null}
      <CommandList ref={listRef} label={label} className="max-h-none min-h-0 flex-1 p-1.5">
        <CommandEmpty className="py-6 text-center text-meta italic text-muted-foreground">{t.managePicker.noResults}</CommandEmpty>
        {pinned.length > 0 ? (
          <CommandGroup className="p-0">
            {pinned.map((item) => <RowOption key={item.id} item={item} current={item.id === value} highlighted={highlight === ROW_VALUE_PREFIX + item.id} onPick={onPick} onDismiss={onDismiss} />)}
          </CommandGroup>
        ) : null}
        {[...groups].map(([group, groupLabel]) => (
          <CommandGroup
            key={group}
            className="p-0"
            heading={<span className="inline-flex items-center gap-1.5 text-eyebrow uppercase">{groupIcons?.[group]}{groupLabel}</span>}
          >
            {items.filter((item) => item.group === group).map((item) => <RowOption key={item.id} item={item} current={item.id === value} highlighted={highlight === ROW_VALUE_PREFIX + item.id} onPick={onPick} onDismiss={onDismiss} />)}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}

/** Single choice for a catalog too long, grouped or searchable for Segmented or SelectMenu: an anchored
 *  combobox dropdown (Radix Popover + cmdk) rendered in place, never in a drawer. A pick commits at once —
 *  there is no Save step — and closes it; Escape or an outside press closes without a change, and focus
 *  returns to the trigger. Search shows only above {@link SEARCH_THRESHOLD} rows, and autofocuses only
 *  off a phone so no keyboard covers the list. Items with `group: ''` are pinned first without a heading.
 *
 *  `iconOnly` draws the trigger as the shared square ghost icon button for a control that STARTS an action
 *  rather than holding a value (moving something somewhere, adding a member): `label` names the action as
 *  its accessible name and tooltip, `icon` is its glyph and `summary` is not drawn. It is the same dropdown,
 *  with the same rows, search and commit-on-pick behaviour. */
export function RowPicker({ label, summary, icon, items, value, onChange, groupIcons, className = '', disabled = false, iconOnly = false, notice, onOpenChange, onClosed }: {
  /** Accessible name of the trigger — what the record is choosing, not what is currently chosen. A caller
   *  whose trigger starts an action passes the action's wording. */
  label: string;
  /** The current pick, rendered as the trigger's visible text. Not drawn by an `iconOnly` trigger. */
  summary: string;
  /** Brand mark or glyph for the current pick, shown before the summary; the sole glyph of `iconOnly`. */
  icon?: ReactNode;
  items: RowPickerItem[];
  value: string;
  onChange: (value: string) => void;
  groupIcons?: Record<string, ReactNode>;
  className?: string;
  /** For a record whose catalog is empty or read-only: the trigger stays visible so the record keeps its
   *  shape, and the caller explains why in the record's status. */
  disabled?: boolean;
  /** Draw the trigger as the shared square ghost icon button, for a control that starts an action. */
  iconOnly?: boolean;
  /** One muted, non-selectable line above the rows for a loading, error or unavailable state. */
  notice?: string;
  /** Fires on every open and close, e.g. to load a catalog lazily on the first opening. */
  onOpenChange?: (open: boolean) => void;
  /** Fires after a pick has closed the dropdown, with focus guaranteed on the trigger, so a follow-up
   *  window opened from here remembers the trigger as the element to return to. It does not fire when
   *  the dropdown is dismissed (Escape, an outside press): those closes only return focus, as Radix does. */
  onClosed?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const mobile = useMobile();
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // cmdk generates the listbox's id; the trigger's `aria-controls` has to name that element, not Radix's frame.
  const [listboxId, setListboxId] = useState<string>();
  const listRef = useCallback((node: HTMLDivElement | null) => {
    listboxRef.current = node;
    setListboxId(node?.id);
  }, []);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchable = items.length > SEARCH_THRESHOLD;
  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  // Set while a pick is closing the dropdown; see `onCloseAutoFocus`.
  const closedByPick = useRef(false);
  const pick = (id: string) => {
    if (id !== value) onChange(id);
    closedByPick.current = true;
    changeOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <RowPickerTrigger
          ref={triggerRef}
          popup="listbox"
          aria-controls={open ? listboxId : undefined}
          label={label}
          summary={summary}
          icon={icon}
          iconOnly={iconOnly}
          disabled={disabled}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // The content is a plain frame: the listbox inside it is the popup the trigger announces
        // (`aria-controls` above points at that listbox, not at this frame).
        role="presentation"
        className="w-[max(var(--radix-popover-trigger-width),--spacing(64))] max-w-[min(--spacing(112),calc(100vw-var(--spacing)*4))] overflow-hidden rounded-xl p-0"
        onOpenAutoFocus={(event) => {
          // Focus the element that carries a role and `aria-activedescendant`, so cmdk's keys work and a
          // screen reader follows the highlight: the search box where a keyboard is not a cost, else the
          // listbox itself. Never the cmdk root, which is a generic element, and which cmdk would also
          // hand focus to the search box from on the next pointer move, raising a phone's keyboard.
          event.preventDefault();
          const target = searchable && !mobile ? inputRef.current : listboxRef.current;
          target?.focus();
          // cmdk only publishes `aria-activedescendant` once the highlight MOVES, and it opens already on
          // the current pick, so name that row from the start; cmdk's own value replaces this on first move.
          const highlighted = listboxRef.current?.querySelector('[cmdk-item][aria-selected="true"]');
          if (target && highlighted) target.setAttribute('aria-activedescendant', highlighted.id);
        }}
        onCloseAutoFocus={(event) => {
          // A pick is the one close that must leave focus on the trigger. Radix skips the return after an
          // outside interaction, so a pick puts it back itself before `onClosed` runs.
          if (!closedByPick.current) return;
          closedByPick.current = false;
          event.preventDefault();
          triggerRef.current?.focus();
          onClosed?.();
        }}
        // The frame is not portaled, so it sits inside whatever label a form Field wraps around the trigger.
        // A browser forwards a click on non-interactive content of a label to the label's control, which is
        // this trigger: the pick would close the dropdown and the forwarded press would open it again.
        onClick={(event) => event.preventDefault()}
      >
        <RowPickerList
          label={label}
          items={items}
          value={value}
          groupIcons={groupIcons}
          notice={notice}
          searchable={searchable}
          listRef={listRef}
          inputRef={inputRef}
          onPick={pick}
          onDismiss={() => changeOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

type EditableSelectionProps = Extract<ComponentProps<typeof ManageSelectionModal>, { selected: Set<string> }>;

/** A multi choice that is not a model catalog (allowed skins, plugin project, plugin, tool and option lists):
 *  a {@link SelectionSummary} card (count, the picked rows with their icons as many as fit one line, the rest
 *  folded into "+N") whose Manage button opens a {@link ManageSelectionModal}: search, group filter, a checkbox per row and
 *  a Save step. Cancel and Esc discard; `onChange` receives the whole next selection once, on Save, and a
 *  rejected async save keeps the modal open for a retry (the caller's own status shows the error). Model
 *  catalogs use {@link ModelSelection}, the model-specific summary and manager.
 *
 *  `label` names the field for the Manage button and is the modal title unless `title` says otherwise. Saved ids
 *  the catalog no longer offers are the caller's job to pin into `items`, so a save never silently drops them.
 *  `emptyLabel` is the count line of an empty selection. */
export function ManagedSelectionField({ label, title, hint, items, value, onChange, groupIcons, countLabel, emptyLabel, ...selection }: {
  label: string;
  hint?: string;
  items: ManageSelectionItem[];
  value: string[];
  onChange: (v: string[]) => void;
  title?: string;
  groupIcons?: Record<string, ReactNode>;
  emptyLabel?: string;
} & Pick<EditableSelectionProps, 'countLabel' | 'emptySelectionHint' | 'saving'>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const countText = countLabel ?? ((n: number) => interpolate(t.managePicker.selectedCount, { n }));
  const picked = new Set(value);
  const samples = items.filter((item) => picked.has(item.id));
  return (
    <>
      <SelectionSummary
        countText={value.length ? countText(value.length) : emptyLabel ?? t.managePicker.none}
        samples={samples}
        moreCount={value.length - samples.length}
        onManage={() => setOpen(true)}
        manageLabel={t.managePicker.manage}
        manageAriaLabel={`${t.managePicker.manage}: ${label}`}
      />
      <ManageSelectionModal
        {...selection}
        title={title ?? label}
        subtitle={hint}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        selected={picked}
        onSave={(next) => onChange([...next])}
        countLabel={countText}
        groupIcons={groupIcons}
      />
    </>
  );
}
